/* global process */
import api from '@/lib/services/general.service'
import { askErrorText, isUnanswered, ASK_FAILED_TEXT, ASK_TIMEOUT_MS } from '@/lib/askFailure'
import { HOME_SCOPE, RESTORE_MESSAGES, chatTitle } from '@/lib/chatSession'
import { normalizeBlocks } from '@/lib/answerBlocks'
import { draftFollowUpBlocks, previousDraft } from '@/lib/draftFollowUp'

export const state = {
  query: '',
  results: [],
  isLoading: false,
  answer: '',
  answerSources: [],
  answerWebSources: [],
  isAsking: false,
  // Chat thread: each turn is { id, question, web, answer, blocks, messageId, sources, webSources, asking }.
  // `blocks` is the answer as the chat draws it (lib/answerBlocks.js — a plain answer is one text
  // block); `messageId` is the journal's id for the answer, where the server sends one.
  conversation: [],
  // A keyword search is on screen. Only `search` sets this — `ask` reuses `query` for the AI
  // question, which is a chat, not a search. It's what tells the shell to enter search focus
  // (see lib/searchMode.js), so it must go false the moment the box is emptied / cleared.
  keywordActive: false,
  // The kept Home conversation (busy-main-bff#21): the server's session id for scope "home", and
  // whether we have already asked for it this page-load. The chat itself still lives in
  // `conversation` — restoring only fills it, so nothing that reads the chat had to change.
  sessionId: null,
  sessionLoaded: false,
  // ── The chat, as a NAMED thing (busy-my-web#450, chat frames "chat result" / "edit title" /
  //    "options" / "delete chat") ────────────────────────────────────────────────────────────────
  //
  // The frames head the page with the conversation's own name and hang Rename / Delete off it. The
  // name the frames show is a SUMMARY of the chat ("Greeting" over the question "Hi", "Best organic
  // eggs for health" over "what is the best organic health eg to eat"), which only the model can
  // write — nothing on the wire carries one today (`chat/sessions` returns the live session's id
  // and its messages, no title). So `title` is what the USER named it, and with no name the derived
  // one (lib/chatSession's `chatTitle`) stands in — the first question, trimmed.
  //
  // 2026-09-15 parity audit — busy-main-bff#42 answers that MISSING BFF FIELD: `GET
  // v1/chat/sessions/{id}` now carries `{ id, title }` and `PATCH v1/chat/sessions/{id} { title }`
  // persists a rename. Bound DEFENSIVELY: the server's title is taken when it sends one, the rename
  // is sent and the LOCAL name stands whatever the route answers — a 404 (a BFF without #42, or a
  // session the server has never heard of) leaves the old local-only behaviour exactly as it was,
  // because a rename that vanishes because a deploy is a day behind is worse than one that is only
  // remembered in this tab.
  title: '',
  // The delete confirmation the frame draws INSIDE the box (red strip + Cancel / Delete).
  deletePrompt: false,
  // ── A NEW conversation inside the same scroll (chat Figma rebuild WP5, frames 18, 33, 34) ──────
  // "+ New conversation" (store/modules/chatSessions `startNew`) marks every turn on screen
  // `archived` and sets `pendingBreak`: the old thread stays drawn, and the next question is pushed
  // with `sessionBreak`, which is what draws the amber "New" above it (Home/ConversationDivider.vue).
  pendingBreak: false,
  // The live conversation's last WEB RESEARCH — `{ question, at }` — set when a question was handed
  // to Research mode (`routeToResearch`): the turn itself is taken back, and this is what lets the
  // rail still say "Web research" under the live row (frame 33).
  liveResearch: null,
}

// ── A running question, and the ■ that ends it (busy-my-web#503) ────────────────────────────────
// The research session keeps exactly this pair (store/modules/research.js): one AbortController per
// turn so the box's stop can reach the request, and a set of ids whose late reply must be dropped
// on arrival — an abort rejects the axios promise, and without the set the `catch` below would
// answer a stopped question with "something went wrong". Module-level rather than in `state`
// because neither is data the UI reads: they are the handles on a request in flight.
const inFlight = new Map()
// …and which RUN of a turn is the current one. A turn can be asked more than once (the answer
// footer's regenerate, an edited question), so "is this reply still wanted?" cannot be asked of the
// turn id alone: a stopped turn that is re-asked would have its second answer dropped as though it
// belonged to the first. Each request takes a token; a stop forgets it, and a reply whose token is
// no longer the turn's current one is dropped on arrival.
let runSeq = 0
const runs = new Map()
const startRun = (id) => { const token = ++runSeq; runs.set(id, token); return token }
const isCurrentRun = (id, token) => runs.get(id) === token
const endRun = (id, token) => {
  if (runs.get(id) !== token) return
  runs.delete(id)
  inFlight.delete(id)
}

// ── Which engine answers (busy-main-bff `v1/intelligent/ask`) ───────────────────────────────────
// A chat question about the user's own mail is answered by busy-intelligent-srv: the BFF runs the
// same scoped retrieval as `ai/ask`, reads and files the scope's kept conversation, and returns the
// same `{ answer, sources, scope }`, so every turn below reads it unchanged. A WEB question stays
// on `ai/ask`. A general question that finds nothing of the user's comes back from Home as
// `webSearchQuery` and goes to Research mode (`routeToResearch`); inside a story or an email it is
// answered in place from the web, with `webSources`. Home is left off the wire on both.
//
// A turn's `scope` is the ANSWER's — `{ requested, effective, fallback, … }` (AskScopeDto) — so
// asking again from a turn (the draft card's regenerate, re-ask, edit) sends what it requested;
// `String()` of that object went out as "[object Object]" and the BFF 400'd it (busy-my-web#625).
export const wireScope = (scope) => {
  if (scope && typeof scope === 'object') {
    if ('requested' in scope) return wireScope(scope.requested)
    // A scope value that prints itself (a typed id) is sent as what it prints.
    return scope.toString !== Object.prototype.toString ? wireScope(String(scope)) : 'home'
  }
  const s = typeof scope === 'string' ? scope.trim() : ''
  return s || 'home'
}
const askRequest = ({ question, timeZoneId, web, scope: asked }) => {
  const scope = wireScope(asked)
  const scoped = scope !== 'home' ? { scope } : {}
  return web
    ? ['ai/ask', { question, timeZoneId, web, ...scoped }]
    : ['intelligent/ask', { question, timeZoneId, ...scoped }]
}

// ── Send a question (and, in development only, answer it from a fixture) ──────────────────────
// `localStorage['busy.chatFixture'] = '<name>'` answers every chat question with
// tests/fixtures/chat/<name>.json (lib/chatFixture.js) — the PLAN §2 block payloads the answer
// renderers are built against before the backend produces them. The whole branch is behind
// `process.env.NODE_ENV !== 'production'`, which the build folds to `false`: the live and test
// bundles contain neither the switch nor the fixtures. With no fixture set, the request goes out
// exactly as before — synchronously, the same promise.
// Every chat ask carries ASK_TIMEOUT_MS (busy-my-web#695): with no clock, a question the server
// never answered waited for as long as the connection lived — the question alone on screen.
const withTimeout = (options) => ({ ...(options || {}), timeout: ASK_TIMEOUT_MS })

// The turn a chat ask lands on when no answer came (#695): the BFF's own sentence when it sent one
// a person can read (its 504 says "Busy couldn't answer — try again."), else the same words. Kept
// as an answer, so the footer's copy / regenerate pair is drawn under it — regenerate is the retry.
const failedTurn = (id, e) => ({ id, answer: e ? askErrorText(e) : ASK_FAILED_TEXT, failed: true, asking: false })

const sendAsk = (args, options) => {
  if (process.env.NODE_ENV !== 'production') {
    let name = ''
    try { name = String(window.localStorage?.getItem?.('busy.chatFixture') || '').trim() } catch { name = '' }
    if (name) {
      return import('@/lib/chatFixture')
        .then(({ loadChatFixture, chatFixtureName }) => loadChatFixture(chatFixtureName()))
        .then((fixture) => (fixture ? { data: fixture } : api.insert(...askRequest(args), withTimeout(options))))
    }
  }
  return api.insert(...askRequest(args), withTimeout(options))
}

// What an answered turn takes from the response — `ask` and `reaskTurn` land the same fields.
// `conversation` + `question` are what an edited / regenerated email draft is read back into its
// card by (lib/draftFollowUp.js, busy-my-web#625).
const answeredTurn = (id, data, { conversation, question } = {}) => ({
  id,
  answer: data.answer || '',
  // The answer as the chat draws it (chat Figma rebuild, PLAN §2): the server's `blocks` when it
  // sends any, else the answer as one text block — and a prose email that follows a draft, as the
  // draft card again.
  blocks: draftFollowUpBlocks(normalizeBlocks(data), { previous: previousDraft(conversation, id, question), question }),
  messageId: data.messageId || null,
  sources: Array.isArray(data.sources) ? data.sources : [],
  webSources: Array.isArray(data.webSources) ? data.webSources : [],
  // The four questions the frame lists under the answer (busy-my-web#450, chat frame "chat
  // result with follow-ups"). The research stream has carried these since it was written
  // (store/modules/research.js); `ai/ask` returns them under the same name where the model
  // offers any, and an answer with none simply draws no block.
  followUps: Array.isArray(data.followUps) ? data.followUps : [],
  // When the answer landed — the rail's "until 6:30 pm" (lib/chatRail `questionsLabel`).
  answeredUtc: new Date().toISOString(),
  // What was actually searched, and whether it widened — the answer says so on screen.
  scope: data.scope || null,
  asking: false,
})

// ── A general question goes to Research mode ─────────────────────────────────────────────────
// When a Home chat question finds nothing in the user's own data and belongs on the web ("what is
// the best chocolate in the world?"), busy-main-bff answers with `webSearchQuery` instead of an
// answer. The question then gets exactly what the globe gives it: the chat turn that was waiting
// for an answer is taken back, and Research mode runs the query — streamed answer, sources, photos,
// videos, products and follow-ups. The BFF filed nothing for it; the research session keeps it.
const routeToResearch = ({ commit, dispatch, state: s }, id, data) => {
  const query = typeof data?.webSearchQuery === 'string' ? data.webSearchQuery.trim() : ''
  if (!query) return false
  // A taken-back turn that opened a new conversation leaves its "New" for the next question.
  if ((s?.conversation || []).find(t => t.id === id)?.sessionBreak) commit('SET_PENDING_BREAK', true)
  commit('SET_LIVE_RESEARCH', { question: query, at: new Date().toISOString() })
  commit('REMOVE_TURN', id)
  dispatch('research/clearDraftEntity', null, { root: true })
  dispatch('research/start', query, { root: true })
  return true
}

export const getters = {
  query: s => s.query,
  results: s => s.results,
  isLoading: s => s.isLoading,
  answer: s => s.answer,
  answerSources: s => s.answerSources,
  answerWebSources: s => s.answerWebSources,
  isAsking: s => s.isAsking,
  conversation: s => s.conversation,
  /**
   * Is a CHAT answer being written right now (busy-my-web#503, chat frame 40011407:276544)?
   *
   * Any turn, not only the last one: the answer footer's regenerate and an edited question both
   * re-ask a turn further up the thread, and the composer is reporting on the chat rather than on
   * its bottom row. The box reads this for its "Thinking…" prompt and for the ■ beside it.
   */
  asking: s => (s.conversation || []).some(t => t && t.asking),
  /** The turn a ■ would stop — the newest one still waiting. Null with nothing running. */
  runningTurnId: s => ([...(s.conversation || [])].reverse().find(t => t && t.asking)?.id) || null,
  keywordActive: s => s.keywordActive,
  sessionId: s => s.sessionId,
  /** What the chat is CALLED — the user's name for it, else the one derived from its first question. */
  title: s => s.title || chatTitle(s.conversation[0]?.question || ''),
  /** Has the user named it themselves? (the header draws the derived name the same way, but a
   *  rename starts from what is on screen, whichever of the two that is) */
  titleIsOwn: s => !!s.title,
  deletePrompt: s => s.deletePrompt,
  pendingBreak: s => !!s.pendingBreak,
  liveResearch: s => s.liveResearch || null,
}

export const actions = {
  /**
   * Bring back the Home conversation (busy-main-bff#21, AI Brain spec §1).
   *
   * Called when Home mounts. Three rules, and each one is a thing that must NOT happen:
   *   • guests are skipped — their identity is derived per browser and dies with it, so there is no
   *     session to come back to and their chat stays in memory exactly as it was before;
   *   • a chat already on screen is never overwritten — a restore that raced an in-flight question
   *     would delete the question the user is watching;
   *   • a failure is silent. Memory is a better chat, not a working one: losing it must never turn
   *     Home into an error page.
   */
  async loadSession({ state: s, commit, dispatch, rootGetters, rootState }) {
    if (s.sessionLoaded) return
    commit('SET_SESSION_LOADED', true)
    if (!rootGetters?.['user/isAuthenticated']) return
    try {
      const res = await api.get('chat/sessions', { scope: HOME_SCOPE, messages: RESTORE_MESSAGES })
      const data = res?.data?.data ?? res?.data ?? {}
      const live = data.live || null
      commit('SET_SESSION', live?.id || null)
      // …and the conversations before it, for the rail (chat Figma rebuild WP5).
      if (rootState?.chatSessions) dispatch('chatSessions/receive', data, { root: true })
      // The session's own NAME (busy-main-bff#42). Fire-and-forget: the greeting is already on
      // screen and a title is not worth making it wait for.
      if (live?.id) dispatch('loadTitle', live.id)
      // The session id is kept so the server's memory stays one thread, but the TURNS are not put
      // back on screen (Pouria, 2026-08-29): a reload — like Clear — opens Home on the greeting,
      // not on yesterday's answer. `lib/chatSession`'s `homeConversation` still maps a restore for anything that
      // wants one later; nothing calls it from here.
    } catch {
      // No history is the old behaviour, and the old behaviour is fine.
    }
  },
  /**
   * Change one block of an answer IN PLACE (chat Figma rebuild, P0 hook): a suggested task that was
   * created becomes `entityCreated` (frame 11), a created task edited from the dock takes its new
   * title and due date (frame 13). `patch` is merged onto the block; its `key` never changes.
   */
  patchBlock({ commit }, { turnId, key, patch } = {}) {
    commit('PATCH_BLOCK', { turnId, key, patch })
  },
  patchEmailBody({ commit }, payload) {
    commit('PATCH_EMAIL_BODY', payload)
  },
  async search({ commit }, query) {
    commit('SET_QUERY', query)
    if (!query || !query.trim()) {
      commit('SET_RESULTS', [])
      commit('SET_KEYWORD_ACTIVE', false)
      return
    }
    commit('SET_KEYWORD_ACTIVE', true)
    commit('SET_LOADING', true)
    try {
      const response = await api.get('timeline-search', { search: query })
      if (response) {
        commit('SET_RESULTS', response.data)
      }
    } catch {
      commit('SET_RESULTS', [])
    } finally {
      commit('SET_LOADING', false)
    }
  },
  // Append a turn to the chat thread: chat / email Q&A, or — when web is true — a web search.
  // Payload may be a string (question) or { question, web, scope }.
  // `scope` bounds the retrieval (busy-main-bff#20) — the caller knows where the user is standing;
  // omitted means home, which is what this action always asked.
  async ask({ commit, dispatch, state: s, rootState }, payload) {
    const question = typeof payload === 'string' ? payload : payload?.question
    const web = typeof payload === 'object' ? !!payload?.web : false
    const scope = wireScope(typeof payload === 'object' ? payload?.scope : null)
    if (!question || !question.trim()) return
    commit('SET_QUERY', question)
    // Questions always go to the LIVE conversation (WP5): a past one open from the rail is put away
    // first, and the live turns come back under the new question.
    if (rootState?.chatSessions?.viewingId) await dispatch('chatSessions/backToLive', null, { root: true })
    // The first question after "+ New conversation" draws the "New" divider — unless there is
    // nothing above it to divide from.
    const sessionBreak = !!s.pendingBreak && s.conversation.length > 0
    commit('SET_PENDING_BREAK', false)
    if (rootState?.chatSessions) dispatch('chatSessions/asked', null, { root: true })

    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
    // `askedUtc` is what the hover row's "12:37 PM" is read from (busy-my-web#450, chat frame
    // "hover over query"); `followUps` is the block the answer frame draws under it.
    commit('PUSH_TURN', { id, question, web, answer: '', blocks: [], messageId: null, sources: [], webSources: [], followUps: [], askedUtc: new Date().toISOString(), asking: true, ...(sessionBreak ? { sessionBreak } : {}) })

    const controller = typeof AbortController === 'function' ? new AbortController() : null
    if (controller) inFlight.set(id, controller)
    const token = startRun(id)
    try {
      const timeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone
      // Home is the default on the server too, so it is left OFF the wire: `scope` appears only
      // when it actually narrows something (see `askRequest`).
      const response = await sendAsk({ question, timeZoneId, web, scope }, controller ? { signal: controller.signal } : {})
      if (!isCurrentRun(id, token)) return
      // Responses are wrapped: { data: { answer, sources, webSources, scope }, isSuccessful }.
      const data = response?.data?.data ?? response?.data ?? {}
      if (routeToResearch({ commit, dispatch, state: s }, id, data)) return
      // A 200 with nothing in it is no answer (#695) — not a blank turn under the question.
      if (isUnanswered(data)) { commit('UPDATE_TURN', failedTurn(id)); return }
      commit('UPDATE_TURN', answeredTurn(id, data, { conversation: s.conversation, question }))
    } catch (e) {
      // A STOPPED question is not a failed one (busy-my-web#503): the abort rejects this promise,
      // and answering it with an apology would put an error where the user pressed ■.
      if (!isCurrentRun(id, token)) return
      commit('UPDATE_TURN', failedTurn(id, e))
    } finally {
      endRun(id, token)
    }
  },

  /**
   * ■ — STOP the chat answer that is being written (busy-my-web#503, chat frame 40011407:276544).
   *
   * The same control the research session's stop is (`research/stopAsk`), on the chat's own leg: the
   * composer reads "Thinking…" while this runs and the send slot is the blue disc with the white
   * square, which calls this. It aborts the request — so the model stops being billed for an answer
   * nobody will read — and closes the turn down with whatever it has, which for a one-shot answer is
   * nothing: the question stays on screen, unanswered, exactly as it was asked.
   *
   * Idempotent, and safe with nothing running: the box asks for a stop on a click, not on a state.
   *
   * @param turnId the turn to stop; omitted → whichever turn is currently waiting.
   * @returns the id of the turn stopped, or null if there was nothing to stop.
   */
  stopAsk({ commit, state: s }, turnId = null) {
    const turn = turnId
      ? (s.conversation || []).find(t => t.id === turnId)
      : [...(s.conversation || [])].reverse().find(t => t && t.asking)
    if (!turn) return null
    const id = turn.id
    // Forget the run: whatever the request does next — answer, or reject with the abort — is no
    // longer this turn's current one, so it is dropped on arrival.
    runs.delete(id)
    const controller = inFlight.get(id)
    inFlight.delete(id)
    try { controller?.abort() } catch { /* already gone */ }
    commit('UPDATE_TURN', { id, asking: false, stopped: true })
    return id
  },
  /**
   * EDIT A QUESTION and ask it again (busy-my-web#450, chat frames "edit query 1/2" → "query
   * changed").
   *
   * The frame replaces the question IN PLACE and answers it again — it is the same turn, re-asked,
   * not a new one at the bottom of the chat. Everything BELOW it goes: those turns were answers to
   * a conversation that no longer happened, and leaving them would put the old thread under a new
   * question. The rail says "Conversation edited" afterwards, which is the same fact.
   */
  async editTurn({ commit, state: s, dispatch }, { id, question } = {}) {
    const text = String(question || '').trim()
    const turn = s.conversation.find(t => t.id === id)
    if (!turn || !text || text === turn.question) return
    commit('TRUNCATE_AFTER', id)
    commit('UPDATE_TURN', {
      id, question: text, answer: '', blocks: [], messageId: null, sources: [], webSources: [], followUps: [], scope: null,
      askedUtc: new Date().toISOString(), edited: true, asking: true,
    })
    await dispatch('reaskTurn', { id, question: text, web: !!turn.web, scope: turn.scope || 'home' })
  },

  /** Ask an EXISTING turn's question again and land the answer on that same turn — what an edit
   *  and the answer footer's regenerate both do. */
  async reaskTurn({ commit, dispatch, state: s }, { id, question, web = false, scope = 'home' } = {}) {
    const turn = s.conversation.find(t => t.id === id)
    const text = String(question || turn?.question || '').trim()
    if (!turn || !text) return
    commit('UPDATE_TURN', { id, answer: '', blocks: [], messageId: null, sources: [], webSources: [], followUps: [], asking: true, stopped: false, failed: false })
    // Re-asking is the same question in flight, so it carries the same ■ (busy-my-web#503).
    const controller = typeof AbortController === 'function' ? new AbortController() : null
    if (controller) inFlight.set(id, controller)
    const token = startRun(id)
    try {
      const timeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone
      const response = await sendAsk({ question: text, timeZoneId, web, scope }, controller ? { signal: controller.signal } : {})
      if (!isCurrentRun(id, token)) return
      const data = response?.data?.data ?? response?.data ?? {}
      if (routeToResearch({ commit, dispatch, state: s }, id, data)) return
      if (isUnanswered(data)) { commit('UPDATE_TURN', failedTurn(id)); return }
      commit('UPDATE_TURN', answeredTurn(id, data, { conversation: s.conversation, question: text }))
    } catch (e) {
      if (!isCurrentRun(id, token)) return
      commit('UPDATE_TURN', failedTurn(id, e))
    } finally {
      endRun(id, token)
    }
  },

  /**
   * The chat's own name, from the server (busy-main-bff#42): `GET v1/chat/sessions/{id}` answers
   * `{ id, title }`.
   *
   * Every failure is silent and leaves what is on screen: no route (a BFF without #42), no title
   * field, an empty title, or a session that is not ours. And a title the USER has already typed is
   * never overwritten — `titleIsOwn` is the user's own name for the chat and it outranks anything
   * the server proposes.
   */
  async loadTitle({ commit, state: s }, sessionId) {
    const id = sessionId || s.sessionId
    if (!id || s.title) return
    try {
      const res = await api.getById('chat/sessions', id)
      const data = res?.data?.data ?? res?.data ?? {}
      const title = String(data?.title || '').trim()
      // Still the same session? A chat cleared while this was in flight must not be renamed by it.
      if (title && s.sessionId === id && !s.title) commit('SET_TITLE', title)
    } catch {
      // 404 / 500 / offline — the derived name (the first question) stands, as it always has.
    }
  },

  /**
   * The header's Rename (chat frame "edit title"). An empty name goes back to the derived one.
   *
   * The name is set LOCALLY first and stays set: `PATCH v1/chat/sessions/{id} { title }`
   * (busy-main-bff#42) persists it where the route exists, and a 404 — the local-only fallback this
   * action shipped with — changes nothing on screen.
   */
  renameConversation({ commit, state: s }, title) {
    const next = String(title || '').trim()
    commit('SET_TITLE', next)
    const id = s.sessionId
    if (!id) return
    api.patch('chat/sessions', id, { title: next }).catch(() => {
      /* local-only fallback: the name is already on screen and stays there */
    })
  },

  /** The header's Delete: it ASKS first, inside the box (chat frame "delete chat"). */
  promptDelete({ commit }) { commit('SET_DELETE_PROMPT', true) },
  cancelDelete({ commit }) { commit('SET_DELETE_PROMPT', false) },
  confirmDelete({ commit, dispatch }) {
    commit('SET_DELETE_PROMPT', false)
    dispatch('clearConversation')
  },

  clear({ commit, dispatch }) {
    commit('SET_QUERY', '')
    commit('SET_RESULTS', [])
    commit('SET_KEYWORD_ACTIVE', false)
    dispatch('clearConversation')
  },
  // Leave keyword search without touching the AI chat thread (emptying the box, clearing the
  // toolbar field, or moving on to a research / AI question).
  clearKeyword({ commit }) {
    commit('SET_QUERY', '')
    commit('SET_RESULTS', [])
    commit('SET_KEYWORD_ACTIVE', false)
  },
  // Clear means gone (Pouria, 2026-08-29): the screen empties AND the live server session is
  // archived, so neither a reload nor the next visit brings the thread back — the next question
  // starts a new one. The archive call is not awaited and its failure is silent: the chat is
  // already off the screen, and an unreachable store must not put it back or raise an error.
  clearConversation({ commit, dispatch, state: s, rootState }) {
    commit('CLEAR_CONVERSATION')
    commit('SET_PENDING_BREAK', false)
    commit('SET_LIVE_RESEARCH', null)
    // No past conversation stays open over a cleared chat, and the live row starts again (WP5).
    if (rootState?.chatSessions) dispatch('chatSessions/reset', null, { root: true })
    // The name goes with the chat it named (busy-my-web#450) — the next question starts a new one.
    commit('SET_TITLE', '')
    commit('SET_DELETE_PROMPT', false)
    const id = s.sessionId
    commit('SET_SESSION', null)
    if (id) api.toggle(`chat/sessions/${id}/archive`).catch(() => {})
  },
}

export const mutations = {
  SET_QUERY(s, query) { s.query = query },
  SET_RESULTS(s, results) { s.results = results },
  SET_LOADING(s, val) { s.isLoading = val },
  SET_ANSWER(s, val) { s.answer = val },
  SET_ANSWER_SOURCES(s, val) { s.answerSources = val },
  SET_ANSWER_WEB_SOURCES(s, val) { s.answerWebSources = val },
  SET_ASKING(s, val) { s.isAsking = val },
  SET_KEYWORD_ACTIVE(s, val) { s.keywordActive = val },
  PUSH_TURN(s, turn) { s.conversation.push(turn) },
  /** A turn taken back — the question went to Research mode instead of being answered here. */
  REMOVE_TURN(s, id) { s.conversation = s.conversation.filter(t => t.id !== id) },
  UPDATE_TURN(s, patch) {
    const t = s.conversation.find(x => x.id === patch.id)
    if (t) Object.assign(t, patch)
  },
  CLEAR_CONVERSATION(s) { s.conversation = [] },
  /** One answer block changed in place — found by its turn and its stable `key`. */
  PATCH_BLOCK(s, { turnId, key, patch } = {}) {
    const turn = s.conversation.find(t => t.id === turnId)
    const block = Array.isArray(turn?.blocks) ? turn.blocks.find(b => b && b.key === key) : null
    if (block && patch && typeof patch === 'object') Object.assign(block, patch, { key: block.key })
  },
  SET_TITLE(s, title) { s.title = title || '' },
  SET_DELETE_PROMPT(s, on) { s.deletePrompt = !!on },
  /** Everything after the given turn — what re-asking an edited question drops (#450). */
  TRUNCATE_AFTER(s, id) {
    const i = s.conversation.findIndex(t => t.id === id)
    if (i >= 0) s.conversation = s.conversation.slice(0, i + 1)
  },
  SET_CONVERSATION(s, turns) { s.conversation = Array.isArray(turns) ? turns : [] },
  SET_SESSION(s, id) { s.sessionId = id || null },
  SET_SESSION_LOADED(s, v) { s.sessionLoaded = !!v },
  SET_PENDING_BREAK(s, on) { s.pendingBreak = !!on },
  SET_LIVE_RESEARCH(s, research) { s.liveResearch = research || null },
  /** "+ New conversation": everything on screen now belongs to the archived conversation. */
  MARK_ARCHIVED(s) { s.conversation.forEach((t) => { t.archived = true }) },
  PATCH_EMAIL_BODY(s, { timelineId, emailId, contentHtml }) {
    const timeline = s.results.find(t => t.id === timelineId)
    if (!timeline) return
    const email = timeline.emailMessages?.find(e => e.id === emailId)
    if (email) email.contentHtml = contentHtml
  },
}
